import { Button, CIActionBtnGroup, MyIcon } from 'ccs-ui-components';
import { useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import SquareBadge from '../../../../../../_components/badges/SquareBadge';
import { IIngressEventMappingModel } from '../../../../../../models/services/IIngressEventMappingModel';
import { AppDispatch, useSelector } from '../../../../../../store/AppStore';
import { deleteEgressEventMapping, deleteEgressEventMappingOfIngress } from '../../../../../../store/reducer/services/EgressEventMappingReducer';
import { deleteIngressEventMapping } from '../../../../../../store/reducer/services/IngressEventMappingReducer';
import { getAssociatedIngressEvents } from '../../../../../../utilities/services/ServiceEventAssociationUtils';
import EgressEventAssociationForm from '../egress-event-association/EgressEventAssociationForm';
import EgressEventForm from '../egress-event-association/EgressEventForm';
import IngressEventAssociationForm from './IngressEventAssociationForm';

import useConfirmDialog from '../../../../../../../common/hooks/ConfirmationDialog';

import classes from './IngressEventAssociationForm.module.scss';

interface IServiceIngressEventFormProps {
  serviceOperationName: string;
  operationVerb: string,
  isView: boolean;
}
interface IServiceIngressEventListProps {
  ingressEventList: IIngressEventMappingModel[];
}

interface IEditIngressEventProps {
  editIngressEvent: (ingressEventName: string, ingressEventVersion: string) => void,
  deleteIngressEvent: (serviceOperationName: string, ingressEventName: string, ingressEventVersion: string) => void,
  isView: boolean,
  ingressEvent: IIngressEventMappingModel,
  showEventForm: boolean,
  index: number,
  selectedIngressEventIndex: number | undefined,

}

const EditIngressEvent = ({ editIngressEvent, deleteIngressEvent, isView, ingressEvent, showEventForm, index, selectedIngressEventIndex }: IEditIngressEventProps) => {
  const useConfirmDialogHook = useConfirmDialog();
  const { t: translate } = useTranslation();
  /**
   * @name confirmDeleteIngressEvent
   * @description: Method will delete the event assocoiation after user confirmation
   * @param operationName
   * @param eventName
   * @param eventVersion
   */
  const confirmDeleteIngressEvent = (operationName: string, eventName: string, eventVersion: string) => {
    useConfirmDialogHook.show({ title: translate('confirm-dialog-prompt-box.title'), message: translate('delete-ingress-event-association.message'), cancelText: translate('confirm-dialog-prompt-box.lbl_close'), okText: translate('confirm-dialog-prompt-box.lbl_confirm') }).then((cnfStatus) => {
      if (cnfStatus) {
        deleteIngressEvent(operationName, eventName, eventVersion);
      }
    });
  };

  return (
    <div className={classes['button-container']}>
      <Button
        onClick={() => editIngressEvent(ingressEvent?.eventName, ingressEvent?.eventVersion)}
        data-testid="btn-edit"
        disabled={isView}
        variant="tertiary"
        className={`${classes['edit-btn']} ${showEventForm && selectedIngressEventIndex === index ? classes['row-bg-class'] : classes['row-no-bg']}`}
      >
        <MyIcon name="editIcon" className={classes['icons-hover']} />
      </Button>
      <Button
        className={`${classes['delete-btn']} ${showEventForm && selectedIngressEventIndex === index ? classes['row-bg-class'] : classes['row-no-bg']}`}
        onClick={() => confirmDeleteIngressEvent(ingressEvent?.serviceOperationName, ingressEvent?.eventName, ingressEvent?.eventVersion)}
        data-testid="btn-delete"
        disabled={isView}
        variant="tertiary"
      >
        <MyIcon name="deleteIcon" className={classes['icons-hover']} />
      </Button>
      {useConfirmDialogHook.render()}
    </div>
  );
};

/**
 * Main Ingress Event association form
 */
const IngressEventForm = ({
  serviceOperationName, isView, operationVerb
}: IServiceIngressEventFormProps) => {
  const { t: translate } = useTranslation();
  const [addEvent, setAddEvent] = useState(false);
  const [addEgressEvent, setAddEgressEvent] = useState(false);
  const [editIngressEventButtonClick, setEditIngressEventButtonClick] = useState(false);
  const [editEgressEventButtonClick, setEditEgressEventButtonClick] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [editSelection, setEditSelection] = useState<string | null>(null);
  const [editEgressVersion, setEditEgressVersion] = useState<string | null>(null);
  const [showEventForm, setShowEventForm] = useState(true);
  const [ingressEventBeingEdited, setIngressEventBeingEdited] = useState('');
  const [ingressEventEditingVersion, setIngressEventEditingVersion] = useState('');
  const [egressAssociatedIngressName, setEgressAssociatedIngressName] = useState('');
  const [egressAssociatedIngressVersion, setEgressAssociatedIngressVersion] = useState('');

  const addEventClicked = () => {
    setAddEvent(true);
  };
  const [selectedIngressEventIndex, setSelectedIngressEventIndex] = useState<number | undefined>(0);
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );

  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );

  /**
   * Calls when save button clicked
   */
  const onSaveEventButtonClick = () => {
    setAddEvent(false);
    setEditIngressEventButtonClick(false);
    setSelectedIngressEventIndex(ingressEventMappingList.filter((item) => item.serviceOperationName === serviceOperationName).length);
    setShowEventForm(true);
  };

  /**
 * Calls when save button clicked
 */
  const onSaveEgressEventButtonClick = () => {
    setAddEgressEvent(false);
    setEditEgressEventButtonClick(false);
  };

  /**
  * Calls when cancel button clicked
  */
  const onCancelEventButtonClick = () => {
    setAddEvent(false);
    setEditIngressEventButtonClick(false);
  };

  /**
   * Calls when delete ingress event button clicked
   * @param serviceOperationName: Service operation for which ingress event to be deleted
   */
  const deleteIngressEvent = (serviceOperationName: string, ingressEventName: string, ingressEventVersion: string) => {
    dispatch(deleteIngressEventMapping({ serviceOperationName, ingressEventName, ingressEventVersion }));
    dispatch(deleteEgressEventMappingOfIngress({ serviceOperationName, ingressEventName, ingressEventVersion }));
  };

  /**
   * calls when edit egress event button clicked
   */
  const editIngressEvent = (ingressEventName: string, ingressEventVersion: string) => {
    setIngressEventBeingEdited(ingressEventName);
    setIngressEventEditingVersion(ingressEventVersion);
    setAddEvent(true);
    setEditIngressEventButtonClick(true);
  };

  const addEgressEventClicked = (ingressName: string, ingressVersion: string) => {
    setAddEgressEvent(true);
    setEgressAssociatedIngressName(ingressName);
    setEgressAssociatedIngressVersion(ingressVersion);
  };
  /**
   * calls when edit egress event button clicked
   */
  const editEgressEvent = (eventId: string, eventVersion: string, ingressName: string, ingressVersion: string) => {
    setEditSelection(eventId);
    setEditEgressVersion(eventVersion);
    addEgressEventClicked(ingressName, ingressVersion);
    setEditEgressEventButtonClick(true);
  };

  /**
   * Calls when delete egress event button clicked
   * @param serviceOperationName: Service operation for which egress event to be deleted
   */
  const deleteEgressEvent = (serviceOperationName: string, eventName: string, ingressName: string, eventVersion: string, ingressEventVersion: string) => {
    dispatch(deleteEgressEventMapping({ serviceOperationName, eventName, ingressName, eventVersion, ingressEventVersion }));
  };

  /**
   * Returns the associated ingress event for service operation
   * @returns event list
   */
  const getAssociatedIngressEvent = () => getAssociatedIngressEvents(ingressEventMappingList, serviceOperationName);

  const caretRightClicked = (index: number) => {
    setSelectedIngressEventIndex(index);
    setShowEventForm(true);
  };

  const caretDownClicked = () => {
    setShowEventForm(false);
    setSelectedIngressEventIndex(undefined);
  };

  const IngressEventAssociationList = ({ ingressEventList }: IServiceIngressEventListProps) =>
    useMemo(
      () => (
        <div className={classes['ingress-event-list']}>
          <Row className={classes['list-header']}>
            <Col sm={1} />
            <Col sm={4}>
              {translate('service_event_association.lbl_ingress_event')}
            </Col>
            <Col sm={3}>
              {translate('service_event_association.lbl_egress_event')}
            </Col>
            <Col sm={4}>
              {translate('service_event_association.lbl_exceptions')}
            </Col>
          </Row>
          {getAssociatedIngressEvent().length < 1 && (
            <Row className={classes['no-records']}>
              {translate('service_event_association.lbl_no_records')}
            </Row>
          )}
          {getAssociatedIngressEvent().length > 0 && (
            <>
              {ingressEventList.filter((item) => item.serviceOperationName === serviceOperationName).map((ingressEvent, index) => (
                <div key={`key-${ingressEvent?.eventName}`}>
                  <Row
                    className={`${classes['event-record']} ${showEventForm && selectedIngressEventIndex === index ? classes['row-bg-class'] : classes['row-no-bg']}`}
                    key={ingressEvent?.eventName}
                  >
                    <Col sm={1}>
                      {(!showEventForm || (selectedIngressEventIndex !== index)) && (
                        <Button className={classes['caret-custom']} variant="outlined" data-testid="show-form-button" onClick={() => caretRightClicked(index)} aria-label="show-form"><MyIcon name="caretRightFillIcon" className={classes['icon-right']} /></Button>
                      )}
                      {showEventForm && selectedIngressEventIndex === index && (
                        <Button className={classes['caret-custom']} variant="outlined" data-testid="hide-form-button" onClick={() => caretDownClicked()} aria-label="hide-form"><MyIcon name="caretDownFillIcon" className={classes['icon-right']} /></Button>
                      )}
                    </Col>
                    <Col sm={4}>
                      <div className={classes.event}>
                        {ingressEvent?.eventName}
                        <div className={classes['version-txt']}>Ver {ingressEvent.eventVersion}</div>
                      </div>
                    </Col>
                    <Col sm={3}>
                      <div className={classes['exception-count']}>
                        <SquareBadge
                          data-testid="ingress-exception-count"
                          count={egressEventMappingList.filter((item) => item.serviceOperationName === serviceOperationName
                            && item.ingressName === ingressEvent.eventName && item.ingressEventVersion === ingressEvent.eventVersion).length}
                        />
                      </div>
                    </Col>
                    <Col sm={3}>
                      <div className={classes['exception-count']}>
                        <SquareBadge
                          data-testid="ingress-exception-count"
                          count={ingressEvent?.exceptions?.length ?? 0}
                        />
                      </div>
                    </Col>
                    <Col sm={1}>
                      {isView ? (
                        <div className={classes['event-btn']}>
                          <CIActionBtnGroup className={classes['view-btn']} buttons={[{ label: 'view', disabled: !isView, key: 'viewIcon', onClick: () => editIngressEvent(ingressEvent?.eventName, ingressEvent?.eventVersion), testId: 'ingress-edit-button' }]} />
                        </div>)
                        : (
                          <EditIngressEvent
                            editIngressEvent={editIngressEvent}
                            deleteIngressEvent={deleteIngressEvent}
                            isView={isView}
                            ingressEvent={ingressEvent}
                            index={index}
                            showEventForm={showEventForm}
                            selectedIngressEventIndex={selectedIngressEventIndex}
                          />)}
                    </Col>
                  </Row>
                  {showEventForm && selectedIngressEventIndex === index
                    && <EgressEventForm
                      serviceOperationName={serviceOperationName}
                      ingressName={ingressEvent?.eventName}
                      isView={isView}
                      editEgressEventButtonClick={editEgressEventButtonClick}
                      addEgressEvent={addEgressEvent}
                      addEgressEventClicked={addEgressEventClicked}
                      editEgressEvent={editEgressEvent}
                      deleteEgressEvent={deleteEgressEvent}
                      ingressEventVersion={ingressEvent?.eventVersion}
                    />}
                </div>
              ))}
            </>
          )}
        </div>
      ),
      []
    );

  return (
    <>
      {!addEvent && !addEgressEvent && (
        <div className={classes.container}>
          <div className={classes['form-header-wrapper']}>
            <div className={classes['form-header']}>
              <div className={classes['header-title']}>
                {translate('service_event_association.lbl_ingress_event')}
                <span>
                  {` (${getAssociatedIngressEvents(
                    ingressEventMappingList,
                    serviceOperationName
                  ).length})`}
                </span>
              </div>
              <Button
                variant="primary"
                className={classes['add-button']}
                onClick={addEventClicked}
                disabled={isView}
                data-testid="btn-add-event"
              >
                {translate('service_event_association.lbl_add_ingress_event')}
              </Button>
            </div>
            <div className={classes['subtitle-header']}>
              <div className={classes.subtitle}>
                {translate('service_event_association.sub_title_ingress_event')}
              </div>
            </div>
          </div>
          {!editIngressEventButtonClick && <IngressEventAssociationList ingressEventList={ingressEventMappingList} />}
        </div>
      )}
      {
        addEvent && (
          <IngressEventAssociationForm
            viewMode={isView}
            addEventClicked={addEvent}
            onCancelButtonClicked={onCancelEventButtonClick}
            onSaveEventButtonClick={onSaveEventButtonClick}
            editIngressEventButtonClick={editIngressEventButtonClick}
            serviceOperationName={serviceOperationName}
            operationVerb={operationVerb}
            ingressEventEditing={ingressEventBeingEdited}
            ingressEventEditingVersion={ingressEventEditingVersion}
          />
        )
      }
      {
        addEgressEvent && (
          <EgressEventAssociationForm
            operationVerb={operationVerb}
            viewMode={isView}
            selectionForEdit={editSelection}
            versionForEdit={editEgressVersion}
            onCancelButtonClicked={onSaveEgressEventButtonClick}
            onSaveEventButtonClick={onSaveEgressEventButtonClick}
            editEgressEventButtonClick={editEgressEventButtonClick}
            serviceOperationName={serviceOperationName}
            ingressName={egressAssociatedIngressName}
            ingressEventVersion={egressAssociatedIngressVersion}
            addBtnClick={addEgressEvent}
          />
        )
      }
    </>
  );
};

export default IngressEventForm;
